import "../register/RegisterPage.scss";
import React, { useEffect, useState } from "react";
import { Button, Input } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { NavLink, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { LogRegLogo } from "../util/LogRegLogo";
import RegisterPageLeft from "../register/RegisterPageLeft";
import { RegStep } from "../register/RegisterPage";
import { drdFlowState } from "../../state/atoms";
import { env } from "../../env/env";
import { isValidEmail, setSiteBannerCookie } from "../../util/utils";
import { logSignIn } from "../../service/AnalyticsService";
import Storage from "../../util/Storage";
import AppApi from "../../api/AppApi";
import MembersApi from "../../api/MembersApi";
import DashboardApi from "../../api/DashboardApi";

interface Props {
  onLogin?: () => void;
}

// DiningRD-specific sign-in page (reached from the MHM button in DiningManager). Unlike the generic
// /account/login, this is login-only: there is no sign-up path and no payment step. Users who aren't
// in MHM yet are pointed at their administrator rather than dropped into account creation.
export default function DrdLoginPage({ onLogin }: Props) {
  const navigate = useNavigate();
  const setDrdFlow = useSetRecoilState(drdFlowState);

  const [step, setStep] = useState<"email" | "password">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<React.ReactNode>("");
  const [loading, setLoading] = useState(false);

  // Drive the DiningRD co-branding on the left marketing panel ("Welcome DiningRD users!").
  useEffect(() => {
    setDrdFlow(true);
  }, [setDrdFlow]);

  // Already signed in — skip the login screen and go straight to the dashboard.
  useEffect(() => {
    if (Storage.getToken()) {
      navigate(env.dashboardPath, { replace: true });
    }
  }, [navigate]);

  const submitDisabled =
    (step === "email" && !(email && isValidEmail(email))) ||
    (step === "password" && !password);

  function submitOnEnter(code: string) {
    if (code === "Enter" && !submitDisabled) {
      onNext();
    }
  }

  async function onEmailNext() {
    setError("");
    setLoading(true);
    try {
      const resp = await AppApi.checkEmail(email);
      if (resp.data) {
        setPassword("");
        setStep("password");
      } else {
        // No account: do NOT start a sign-up flow — direct the user to their administrator.
        setError("No account found. Try a different email or reach out to your administrator for access.");
      }
    } catch (e) {
      console.error(e);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function onPasswordNext() {
    setError("");
    setLoading(true);
    try {
      const loginResp = await AppApi.login({ username: email, password: password.trim() });
      const resp = loginResp.data;

      if (resp.error) {
        let errMsg: React.ReactNode = "We couldn't find that login / password combination.";
        if ("paused" === resp.code) {
          errMsg = "This account is paused. Please contact your account owner.";
        } else if ("canceled" === resp.code) {
          errMsg = "This account is cancelled. Please contact your account owner.";
        } else if ("declined" === resp.code) {
          errMsg = "This account is declined. Please contact your account owner.";
        } else if ("blocked" === resp.code) {
          errMsg = (
            <div style={{ textAlign: "center" }}>
              <b>This account is blocked.</b>
              <br />
              Please contact our customer service team.
            </div>
          );
        }
        setError(errMsg);
        setLoading(false);
        return;
      }

      if (resp.token) {
        Storage.setToken(resp.token);
      }
      logSignIn(resp.mid + "");
      setSiteBannerCookie(true);
      try {
        await MembersApi.increaseMemberLoginCount(resp.mid!);
      } catch (e) {
        console.error(e);
      }
      // Flag the member as a DiningRD user, mirroring the drd email login in RegisterPage.
      if (resp.mid) {
        MembersApi.updateMemberDiningRDFlag(resp.mid, true);
      }

      onLogin?.();
      // Touch status so App bootstrap has fresh member/location state before we land on the dashboard.
      await DashboardApi.status();
      navigate(env.dashboardPath);
    } catch (e) {
      console.error(e);
      setError("We couldn't find that login / password combination.");
      setLoading(false);
    }
  }

  function onNext() {
    if (step === "email") {
      onEmailNext();
    } else {
      onPasswordNext();
    }
  }

  return (
    <div className={"reg-page"}>
      <div className={"left"}>
        <RegisterPageLeft step={RegStep.Login} />
      </div>
      <div className={"right"}>
        <div className={"step-type step full"}>
          {step === "password" && (
            <div className={"back"} onClick={() => { setError(""); setStep("email"); }}>
              <LeftOutlined />
            </div>
          )}
          <LogRegLogo />
          <h2>Log in with email</h2>
          {step === "email"
            ? <h5>We'll check if you have an account.</h5>
            : <h5>Enter your password for {email}</h5>}

          {error && <h6 className={"err"}>{error}</h6>}

          <div className="login-buttons">
            {step === "email" && (
              <Input
                type={"email"}
                placeholder={"Email"}
                value={email}
                size={"large"}
                autoFocus
                autoComplete={"username"}
                onChange={ev => setEmail(ev.target.value?.trim().toLowerCase())}
                onKeyUp={ev => submitOnEnter(ev.key)}
              />
            )}
            {step === "password" && (
              <Input.Password
                placeholder={"Password"}
                value={password}
                size={"large"}
                autoFocus
                autoComplete={"current-password"}
                onChange={ev => setPassword(ev.target.value?.trim())}
                onKeyUp={ev => submitOnEnter(ev.key)}
              />
            )}
            <Button
              size={"large"}
              className={"primary"}
              onClick={onNext}
              loading={loading}
              disabled={submitDisabled}>
              Next
            </Button>
          </div>

          <div className={`f-pass ${step !== "password" ? "hidden" : ""}`}>
            <NavLink to={"/account/password"} state={{ email }}>Forgot your password?</NavLink>
          </div>
        </div>
      </div>
    </div>
  );
}
